/**
 * AppSettings — App-wide settings tab content (admin).
 * Contains: transfer approval toggle, waste log reminder settings.
 */
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ArrowsLeftRight, ClockClockwise, Bell } from "@phosphor-icons/react";

export function AppSettings({
  isAdmin,
  appSettings, savingSettings,
  onToggleTransferApproval, onToggleWasteReminder, onReminderTimeChange,
  onSendTestReminder
}) {
  return (
    <>
      {/* Transfer Settings */}
      <Card className="border rounded-xl">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg tracking-wide flex items-center gap-2">
            <ArrowsLeftRight size={20} weight="duotone" className="text-primary" />
            TRANSFER SETTINGS
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
            <div>
              <p className="font-medium">Require Transfer Approvals</p>
              <p className="text-sm text-muted-foreground mt-1">
                When enabled, inter-location transfers require admin approval before completing.
                When disabled, transfers are auto-approved.
              </p>
            </div>
            <Switch
              data-testid="transfer-approval-toggle"
              checked={appSettings.require_transfer_approval}
              onCheckedChange={onToggleTransferApproval}
              disabled={savingSettings}
            />
          </div>
        </CardContent>
      </Card>

      {/* Waste Log Reminder Settings - Admin Only */}
      {isAdmin && (
        <Card className="border rounded-xl">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg tracking-wide flex items-center gap-2">
              <ClockClockwise size={20} weight="duotone" className="text-orange-500" />
              WASTE LOG REMINDERS (SYSTEM)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div>
                <p className="font-medium">Enable Daily Waste Reminders</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Automatically send reminders to all users to log daily waste.
                </p>
              </div>
              <Switch
                data-testid="waste-reminder-toggle"
                checked={appSettings.waste_reminder_enabled}
                onCheckedChange={onToggleWasteReminder}
                disabled={savingSettings}
              />
            </div>

            {appSettings.waste_reminder_enabled && (
              <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                <div>
                  <p className="font-medium">Reminder Time</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Time of day to send waste log reminders (PST).
                  </p>
                </div>
                <Select
                  value={appSettings.waste_reminder_time}
                  onValueChange={onReminderTimeChange}
                  disabled={savingSettings}
                >
                  <SelectTrigger className="w-32" data-testid="reminder-time-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="14:00">2:00 PM</SelectItem>
                    <SelectItem value="15:00">3:00 PM</SelectItem>
                    <SelectItem value="16:00">4:00 PM</SelectItem>
                    <SelectItem value="17:00">5:00 PM</SelectItem>
                    <SelectItem value="18:00">6:00 PM</SelectItem>
                    <SelectItem value="19:00">7:00 PM</SelectItem>
                    <SelectItem value="20:00">8:00 PM</SelectItem>
                    <SelectItem value="21:00">9:00 PM</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-center justify-between p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div>
                <p className="font-medium text-orange-800">Send Test Reminder</p>
                <p className="text-sm text-orange-600 mt-1">
                  Immediately send a waste log reminder to all location users.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={onSendTestReminder}
                className="border-orange-300 text-orange-700 hover:bg-orange-100"
                data-testid="send-test-reminder-btn"
              >
                <Bell size={16} className="mr-2" />
                Send Now
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}
